import axiosInstance, { getAPIURL } from "../api/axiosInstance";
import {
  getMyExplorerOnboarding,
  saveExplorerOnboarding,
  saveExplorerProfile,
} from "../api/explorerOnboardingApi";
import { isLoggedIn } from "../auth/authStorage";
import { formatCityCountryLabel } from "../utils/cityAsyncSelectOptions";
import {
  clearExplorerPendingOnboarding,
  getExplorerEventId,
  getExplorerFlow,
  getExplorerLeadProfile,
  getExplorerLocalProfile,
  getExplorerPendingOnboarding,
  type ExplorerLocalProfile,
} from "./explorerSession";

const OBJECTIVES_FOR_ATTENDING_KEY = "objectivesForAttending";

export type ExplorerProfilePersistOptions = {
  segment?: string;
  objectives?: string;
  discoveryAnswers?: Record<string, string>;
};

function strVal(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** Registration / checkout draft (`UserData`) — used when explorer session was not API-saved. */
function readRegisterProfileFromStorage(): Partial<ExplorerLocalProfile> {
  try {
    const raw = localStorage.getItem("UserData");
    if (!raw) return {};
    const p = JSON.parse(raw) as Record<string, unknown>;
    const authEmail = localStorage.getItem("auth_email")?.trim() ?? "";
    return {
      firstName: strVal(p.firstName),
      lastName: strVal(p.lastName),
      email: strVal(p.email) || authEmail,
      phoneNumber: strVal(p.phoneNumber),
      city: strVal(p.city),
      country: strVal(p.country),
      jobTitle: strVal(p.personalPositionCompany) || strVal(p.jobTitle),
      companyName: strVal(p.businessName),
      sector: strVal(p.sector),
    };
  } catch {
    return {};
  }
}

/**
 * Step-1 profile from session, lead buffer, or register UserData (same auth email only).
 */
export function resolveProfileForSync(): ExplorerLocalProfile | null {
  const local = getExplorerLocalProfile();
  const lead = getExplorerLeadProfile();
  const stored = readRegisterProfileFromStorage();
  const authEmail = localStorage.getItem("auth_email")?.trim().toLowerCase() ?? "";

  const firstName =
    strVal(local?.firstName) || strVal(lead?.firstName) || strVal(stored.firstName);
  const lastName =
    strVal(local?.lastName) || strVal(lead?.lastName) || strVal(stored.lastName);

  let email =
    strVal(local?.email) || strVal(lead?.email) || strVal(stored.email) || authEmail;
  email = email.trim().toLowerCase();

  if (!firstName || !lastName || !email) return null;

  if (authEmail && email !== authEmail) {
    if (local?.email?.trim().toLowerCase() === authEmail) {
      email = authEmail;
    } else if (lead?.email?.trim().toLowerCase() === authEmail) {
      email = authEmail;
    } else if (strVal(stored.email).toLowerCase() === authEmail) {
      email = authEmail;
    } else if (isLoggedIn()) {
      email = authEmail;
    } else {
      return null;
    }
  }

  return {
    firstName,
    lastName,
    email,
    phoneNumber: local?.phoneNumber ?? stored.phoneNumber,
    city: local?.city ?? stored.city,
    country: local?.country ?? stored.country,
    jobTitle: local?.jobTitle ?? stored.jobTitle,
    companyName: local?.companyName ?? stored.companyName,
    sector: local?.sector ?? stored.sector,
    entrySource: (local?.entrySource ?? "event").trim() || "event",
    eventId: local?.eventId,
    gdprConsent: local?.gdprConsent ?? isLoggedIn(),
    marketingConsent: Boolean(local?.marketingConsent),
  };
}

function buildProfilePayload(
  local: ExplorerLocalProfile,
  options?: ExplorerProfilePersistOptions,
) {
  const pending = getExplorerPendingOnboarding();
  const segment = options?.segment ?? pending?.segment;
  const discoveryAnswers =
    options?.discoveryAnswers ?? pending?.discoveryAnswers;
  const objectives =
    options?.objectives ??
    discoveryAnswers?.[OBJECTIVES_FOR_ATTENDING_KEY] ??
    "";

  const eventIdStr = getExplorerEventId();
  const eventIdNum = eventIdStr ? parseInt(eventIdStr, 10) : NaN;
  const safeEventId = Number.isFinite(eventIdNum) ? eventIdNum : undefined;

  // Explorer DB has no Country column — persist as "City, Country" (forms split on read).
  const cityForApi = formatCityCountryLabel(local.city, local.country) || local.city;

  return {
    firstName: local.firstName,
    lastName: local.lastName,
    phoneNumber: local.phoneNumber,
    city: cityForApi,
    jobTitle: local.jobTitle,
    companyName: local.companyName,
    sector: local.sector,
    entrySource: local.entrySource,
    eventId: local.eventId ?? safeEventId,
    gdprConsent: local.gdprConsent,
    marketingConsent: local.marketingConsent,
    attendeeType: segment,
    objectives,
    discoveryAnswers,
  };
}

/**
 * Persist step-1 (+ optional step-2) explorer profile to the API (Users table).
 * Requires a signed-in account — anonymous public-profile is retired.
 */
export async function persistExplorerProfileFromSession(
  options?: ExplorerProfilePersistOptions,
): Promise<boolean> {
  if (!isLoggedIn()) return false;

  const local = resolveProfileForSync();
  if (!local) return false;

  const body = buildProfilePayload(local, options);
  await saveExplorerProfile(body);
  return true;
}

/** After sign-in: push step-1/2 onto authenticated profile (segment optional). */
export async function syncExplorerProfileAfterAuth(): Promise<boolean> {
  const pending = getExplorerPendingOnboarding();
  return persistExplorerProfileFromSession({
    segment: pending?.segment,
    discoveryAnswers: pending?.discoveryAnswers,
    objectives: pending?.discoveryAnswers?.[OBJECTIVES_FOR_ATTENDING_KEY],
  });
}

export function buildExplorerLoginRedirectQuery(
  local: ExplorerLocalProfile,
  redirectPath: string,
  mode: "login" | "register" = "login",
): string {
  const q = new URLSearchParams();
  q.set("redirect", redirectPath);
  q.set("explorer", "1");
  q.set("mode", mode);
  if (local.email) q.set("email", local.email);
  if (local.firstName) q.set("prefillFirstName", local.firstName);
  if (local.lastName) q.set("prefillLastName", local.lastName);
  return q.toString();
}

export class ExplorerProfileSyncError extends Error {
  constructor(message = "Could not save your explorer profile. Please try again.") {
    super(message);
    this.name = "ExplorerProfileSyncError";
  }
}

function readRecordNames(
  record: unknown,
  firstKey = "firstName",
  lastKey = "lastName",
): { firstName: string; lastName: string } {
  const row = record as Record<string, unknown> | null | undefined;
  return {
    firstName: strVal(row?.[firstKey] ?? row?.FirstName),
    lastName: strVal(row?.[lastKey] ?? row?.LastName),
  };
}

export function hasCompleteExplorerNames(firstName: string, lastName: string): boolean {
  return Boolean(firstName.trim() && lastName.trim());
}

/** Active explorer wizard/onboarding session (event link, step-1 buffer, or pending step-2). */
export function isExplorerOnboardingSession(): boolean {
  return Boolean(
    getExplorerEventId() ||
      getExplorerLocalProfile() ||
      getExplorerPendingOnboarding()?.segment,
  );
}

export function isExplorerProtectedDestination(path: string): boolean {
  const normalized = path.split("?")[0].trim();
  return (
    normalized.startsWith("/confirm-booking") ||
    normalized.startsWith("/explorer/") ||
    normalized === "/event-booking"
  );
}

/** Unified auth entry (replaces retired `/explorer/signup`). */
export function explorerSignupPath(): string {
  if (isLoggedIn()) {
    return "/register-user";
  }

  const local = getExplorerLocalProfile();
  const q = new URLSearchParams();
  q.set("mode", "register");
  q.set("explorer", "1");
  q.set("redirect", "/explorer/segmentation");
  if (local?.email) q.set("email", local.email);
  if (local?.firstName) q.set("prefillFirstName", local.firstName);
  if (local?.lastName) q.set("prefillLastName", local.lastName);
  return `/?${q.toString()}`;
}

export async function fetchPortalUserNames(
  objectId: string,
): Promise<{ firstName: string; lastName: string } | null> {
  try {
    const { data } = await axiosInstance.get(
      getAPIURL(`/User/ByObjectId/${encodeURIComponent(objectId.trim())}`),
    );
    const user = data?.user ?? data?.User;
    if (!user) return null;
    return readRecordNames(user);
  } catch {
    return null;
  }
}

export async function fetchExplorerRecordNames(): Promise<{
  firstName: string;
  lastName: string;
} | null> {
  if (!isLoggedIn()) return null;
  try {
    const me = await getMyExplorerOnboarding();
    if (!me?.explorer) return null;
    return readRecordNames(me.explorer);
  } catch {
    return null;
  }
}

/** Names present on User or Explorer after session sync attempts. */
export async function hasPersistedExplorerIdentity(objectId?: string): Promise<boolean> {
  const oid = objectId?.trim() || localStorage.getItem("objectId")?.trim() || "";
  if (oid) {
    const userNames = await fetchPortalUserNames(oid);
    if (userNames && hasCompleteExplorerNames(userNames.firstName, userNames.lastName)) {
      return true;
    }
  }
  const explorerNames = await fetchExplorerRecordNames();
  return Boolean(
    explorerNames &&
      hasCompleteExplorerNames(explorerNames.firstName, explorerNames.lastName),
  );
}

/**
 * Explorer auth/onboarding must not proceed without first + last name saved or ready to save.
 * Legacy portal users booking directly are not gated here (non-explorer destinations).
 */
export async function resolveExplorerGuardedPath(
  objectId: string | undefined,
  intendedPath: string,
  options?: { explorerContext?: boolean },
): Promise<string> {
  const explorerContext =
    options?.explorerContext ??
    ((typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("explorer") === "1") ||
      isExplorerOnboardingSession());

  if (!explorerContext || !isExplorerProtectedDestination(intendedPath)) {
    return intendedPath;
  }

  if (resolveProfileForSync()) {
    return intendedPath;
  }

  const hasNames = await hasPersistedExplorerIdentity(objectId);
  return hasNames ? intendedPath : explorerSignupPath();
}

/** Full post-auth explorer sync: profile (step 1) then segmentation save (step 2 when present). */
export async function syncExplorerStateAfterAuth(): Promise<void> {
  const pending = getExplorerPendingOnboarding();
  const flow = getExplorerFlow();
  const eventId = getExplorerEventId();
  const eventIdNum = eventId ? parseInt(eventId, 10) : NaN;
  const safeEventId = Number.isFinite(eventIdNum) ? eventIdNum : undefined;
  const inExplorer = isExplorerOnboardingSession();

  const profileSaved = await syncExplorerProfileAfterAuth();

  if (!profileSaved && pending?.segment) {
    throw new ExplorerProfileSyncError();
  }

  if (!profileSaved && inExplorer) {
    const objectId = localStorage.getItem("objectId")?.trim() || undefined;
    const hasNames = await hasPersistedExplorerIdentity(objectId);
    if (!hasNames) {
      throw new ExplorerProfileSyncError(
        "Please complete your explorer profile before continuing.",
      );
    }
  }

  if (!profileSaved && !pending?.segment) {
    return;
  }

  if (pending?.segment) {
    await saveExplorerOnboarding({
      segment: pending.segment,
      flowType: flow,
      discoveryAnswers: pending.discoveryAnswers,
      eventId: safeEventId,
    });
    clearExplorerPendingOnboarding();
  }
}

let eventBookingProfileSyncInFlight: Promise<boolean> | null = null;

/** True when session/UserData may hold explorer profile not yet on the API. */
export function shouldSyncExplorerProfileForEventBooking(): boolean {
  if (!isLoggedIn()) return false;
  return Boolean(
    resolveProfileForSync() ||
      getExplorerLocalProfile() ||
      getExplorerPendingOnboarding()?.segment ||
      getExplorerLeadProfile(),
  );
}

/**
 * Register-user abandon → sidebar Events → book: push session/UserData profile (+ step 2)
 * to Explorer/Users before AddEventAttendee merge runs.
 */
export async function ensureExplorerProfileSyncedForEventBooking(): Promise<boolean> {
  if (!isLoggedIn() || !shouldSyncExplorerProfileForEventBooking()) {
    return false;
  }

  if (eventBookingProfileSyncInFlight) {
    return eventBookingProfileSyncInFlight;
  }

  eventBookingProfileSyncInFlight = (async () => {
    const pending = getExplorerPendingOnboarding();
    const flow = getExplorerFlow();
    const eventIdStr = getExplorerEventId();
    const eventIdNum = eventIdStr ? parseInt(eventIdStr, 10) : NaN;
    const safeEventId = Number.isFinite(eventIdNum) ? eventIdNum : undefined;

    let profileSaved = false;
    try {
      profileSaved = await persistExplorerProfileFromSession({
        segment: pending?.segment,
        discoveryAnswers: pending?.discoveryAnswers,
        objectives: pending?.discoveryAnswers?.[OBJECTIVES_FOR_ATTENDING_KEY],
      });
    } catch {
      profileSaved = false;
    }

    if (pending?.segment) {
      try {
        await saveExplorerOnboarding({
          segment: pending.segment,
          flowType: flow,
          discoveryAnswers: pending.discoveryAnswers,
          eventId: safeEventId,
        });
        clearExplorerPendingOnboarding();
      } catch {
        /* profile row may still have been upserted */
      }
    }

    return profileSaved;
  })().finally(() => {
    eventBookingProfileSyncInFlight = null;
  });

  return eventBookingProfileSyncInFlight;
}
